import { inject, Injectable } from '@angular/core';
import {
  CheckoutTabsDisabled,
  ProcessOrderResponse,
  CheckoutTab,
  RentalData,
  SaveAddressDto,
  UserAddress,
  STRData,
  ProcessShortTermRentalDto,
  STRGetTaxAmountDto,
  STRCheckoutTotals
} from '../models';
import { NgxAimService } from '../ngx-aim.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { AddressType, CheckoutTabNames, CheckoutType, GAEventAction, STRInterval } from '../enums';
import { STRTaxAmountDto } from '../models/dto/tax.dto';
import { HttpErrorService } from './http-error.service';
import { PaymentService } from './payment.service';
import { GoogleAnalyticsService } from './google-analytics.service';
import { GenericCheckoutService } from './generic-checkout.service';

@Injectable({
  providedIn: 'root'
})
export class ShortTermRentalCheckoutService implements GenericCheckoutService {
  ngxAimService = inject(NgxAimService);
  http = inject(HttpClient);
  httpErrorService = inject(HttpErrorService);
  paymentService = inject(PaymentService);
  gaService = inject(GoogleAnalyticsService);

  private baseUrl = this.ngxAimService.getApiUrl();
  private rentalDataSubject = new BehaviorSubject<RentalData>({
    CategoryNumber: 0,
    SubNumber: 0,
    subCategory: {},
  });
  public rentalData$ = this.rentalDataSubject.asObservable();
  public setRentalData(rentalData: RentalData) {
    this.rentalDataSubject.next(rentalData);
    localStorage.setItem('rental', JSON.stringify(rentalData));
  }
  public getRentalData() {
    return this.rentalDataSubject.value;
  }

  public tabs: CheckoutTab[] = [];

  private tabIndexSubject = new BehaviorSubject<number>(0);
  public tabIndex$ = this.tabIndexSubject.asObservable();
  public setTabIndex(index: number) {
    this.tabIndexSubject.next(index);
  }
  public setTabIndexByHeader(header: CheckoutTabNames) {
    const tab = this.tabs.find((tab) => tab.header === header);
    if (tab) {
      this.tabIndexSubject.next(tab.index);
    } else {
      console.error(`unable to find tab header: ${header}`);
    }
  }

  private tabsDisabledSubject = new BehaviorSubject<CheckoutTabsDisabled>({
    [CheckoutTabNames.CHECK_AVAILABILITY]: false,
    [CheckoutTabNames.BILLING_ADDRESS]: true,
    [CheckoutTabNames.PAYMENT]: true,
    [CheckoutTabNames.OVERVIEW]: true,
  });
  public tabsDisabed$ = this.tabsDisabledSubject.asObservable();
  public setTabIsDisabled(tabIndex: CheckoutTabNames, isDisabled: boolean) {
    const tabs = this.tabsDisabledSubject.value;
    tabs[tabIndex] = isDisabled;
    this.tabsDisabledSubject.next(tabs);
  }

  private strDataSubject = new BehaviorSubject<STRData>({
    rate: {
      HeaderDesc: '',
      HeaderNotes: '',
      Desc: '',
      IntervalType: STRInterval.HOURS,
      Group: '',
      Notes: '',
      Amt: 0,
      TimeInterval: 0,
    },
    pickupDateString: '',
    pickupTime: { id: -1, dayOfWeek: 0, time: '00:00', displayTime: '12:00 AM', enabled: true },
    returnDateString: '',
    returnTime: { id: -1, dayOfWeek: 0, time: '00:00', displayTime: '12:00 AM', enabled: true },
    strAmount: {
      total: 0,
      IntervalType: '',
      length: 0,
      datePipeOption: 'short',
      rateAmt: 0,
    }
  });
  public strData$ = this.strDataSubject.asObservable();
  public setStrData(strData: STRData) {
    this.strDataSubject.next(strData);
    this.calculateTotals();
  }

  private isLoadingTotalsSubject = new BehaviorSubject<boolean>(false);
  public isLoadingTotals$ = this.isLoadingTotalsSubject.asObservable();
  private totalsSubject = new BehaviorSubject<STRCheckoutTotals>({
    items: 0,
    shipping: 0,
    tax: 0,
    total: 0,
    tax2: 0,
    taxTotal: 0,
  });
  public totals$ = this.totalsSubject.asObservable();

  public updateTotals() {
    this.calculateTotals();
  }

  async calculateTotals() {
    this.isLoadingTotalsSubject.next(true);
    const itemTotal = this.strDataSubject.value.strAmount?.total || 0;
    const tax = await this.getTaxAmount({
      subTotal: itemTotal,
      CategoryNumber: this.getRentalData().CategoryNumber,
      SubNumber: this.getRentalData().SubNumber,
    });
    const shipping = 0;

    this.totalsSubject.next({
      items: this.strDataSubject.value.strAmount?.total || 0,
      shipping: shipping,
      tax: tax.amount,
      total: itemTotal + shipping + tax.amount + tax.amount2,
      tax2: tax.amount2,
      taxTotal: tax.total,
    });
    this.isLoadingTotalsSubject.next(false);
  }

  constructor() {
    if (typeof localStorage === 'undefined') {
      return;
    }

    const rawSavedCatSubCat = localStorage.getItem('rental');
    if (rawSavedCatSubCat) {
      const savedCatSubCat = JSON.parse(rawSavedCatSubCat) as RentalData;
      this.setRentalData(savedCatSubCat);
    }
  }

  saveAddress(address: SaveAddressDto): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/users/save-address`, address);
  }

  getAddress(type: AddressType): Observable<UserAddress> {
    return this.http.get<UserAddress>(`${this.baseUrl}/users/get-address`, {
      params: new HttpParams()
        .set('type', type)
    });
  }

  getTaxAmount(dto: STRGetTaxAmountDto): Promise<STRTaxAmountDto> {
    return new Promise<STRTaxAmountDto>((resolve) => {
      this.http.post<STRTaxAmountDto>(`${this.baseUrl}/short-term-rental/tax`, dto).subscribe({
        next: (res) => resolve(res),
        error: (err) => {
          this.httpErrorService.onHttpError(err, 'Could not calculate sales tax');
          resolve({ amount: 0, amount2: 0, total: 0 });
        }
      });
    });
  }

  checkout(): Observable<ProcessOrderResponse> {
    const rentalData = this.rentalDataSubject.value;
    const dto: ProcessShortTermRentalDto = {
      CategoryNumber: rentalData.CategoryNumber,
      SubNumber: rentalData.SubNumber,
      strData: this.strDataSubject.value,
      cardData: this.paymentService.cardData,
    }
    return this.http.post<ProcessOrderResponse>(`${this.baseUrl}/short-term-rental/checkout`, dto);
  }

  emitGaAddPaymentInfo(): void {
    this.gaService.emitEvent(GAEventAction.ADD_PAYMENT_INFO, {
      currency: 'USD',
      value: this.totalsSubject.value.items,
      payment_type: 'Credit Card',
      checkout_type: CheckoutType.SHORT_TERM_RENTAL,
      items: [{
        item_id: this.strDataSubject.value.rate.Desc,
        item_name: this.strDataSubject.value.rate.Group || this.strDataSubject.value.rate.Desc,
        price: this.totalsSubject.value.items,
        quantity: 1,
      }],
    });
  }

  emitGaAddShippingInfo(): void {
    this.gaService.emitEvent(GAEventAction.ADD_SHIPPING_INFO, {
      currency: 'USD',
      value: this.totalsSubject.value.items,
      payment_type: 'Credit Card',
      checkout_type: CheckoutType.SHORT_TERM_RENTAL,
      items: [{
        item_id: this.strDataSubject.value.rate.Desc,
        item_name: this.strDataSubject.value.rate.Group || this.strDataSubject.value.rate.Desc,
        price: this.totalsSubject.value.items,
        quantity: 1,
      }],
    });
  }

  public reset() {
    this.paymentService.reset();
    localStorage.removeItem('rental');
    this.setRentalData({
      CategoryNumber: 0,
      SubNumber: 0,
      subCategory: {},
    });
    this.tabIndexSubject.next(0);
    this.tabsDisabledSubject.next({
      [CheckoutTabNames.CHECK_AVAILABILITY]: false,
      [CheckoutTabNames.BILLING_ADDRESS]: true,
      [CheckoutTabNames.PAYMENT]: true,
      [CheckoutTabNames.OVERVIEW]: true,
    });

    this.totalsSubject.next({
      items: 0,
      shipping: 0,
      tax: 0,
      total: 0,
      tax2: 0,
      taxTotal: 0,
    });
  }
}
